import React, { useState } from "react";
import { Modal, Button, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { colors, constants, getActivityName, logEducatorDashboard, LogLevel } from "utils";
import { getNodeByStandardPrefix } from "utils/standards";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";
import pie_chart_32 from "assets/pie-chart_32.svg";

export const CopyGroupSettingsModal = ({
  isOpen,
  onClose,
  sourceCode,
  sourceConfig,
  allCodesToConfig,
  standards,
  onSuccess
}) => {
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const getStandardInfo = (config) => {
    const isCrazyCalculator = config.activity_type === constants.CRAZY_CALCULATOR_ENUM;

    if (isCrazyCalculator) {
      return {
        code: null,
        name: getActivityName(constants.CRAZY_CALCULATOR_ENUM),
        isCrazyCalculator: true
      };
    }

    if (!config.standard_prefix || config.activity_type !== constants.CONSENSUS_ENUM) {
      return null;
    }

    if (!standards || standards.length === 0) {
      return null;
    }

    const node = getNodeByStandardPrefix(standards, config.standard_prefix);

    if (!node || (!node.standard && !node.cluster && !node.domain && !node.grade)) {
      return null;
    }

    let type = 'STANDARD';
    let name = '';

    if (node.standard) {
      type = 'STANDARD';
      name = node.standard.name;
    } else if (node.cluster) {
      type = 'CLUSTER';
      name = node.cluster.name;
    } else if (node.domain) {
      type = 'DOMAIN';
      name = node.domain.name;
    } else if (node.grade) {
      type = 'GRADE';
      name = `Grade ${node.grade}`;
    }

    return {
      code: config.standard_prefix,
      name: name,
      type: type,
      isCrazyCalculator: false
    };
  };

  const getSessionLengthLabel = (sessionLength) => {
    const options = [
      { label: "10-15 Minutes", value: 10 },
      { label: "15-20 Minutes", value: 15 },
      { label: "20-25 Minutes", value: 20 },
      { label: "25-30 Minutes", value: 25 },
      { label: "30-35 Minutes", value: 30 },
    ];
    return options.find(opt => opt.value === sessionLength)?.label || "15-20 Minutes";
  };

  const toggleCodeSelection = (code) => {
    setSelectedCodes(prev =>
      prev.includes(code)
        ? prev.filter(c => c !== code)
        : [...prev, code]
    );
  };

  const handleApply = async () => {
    if (selectedCodes.length === 0) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Copy settings to each selected group code, preserving each target's classroom_id
      await Promise.all(
        selectedCodes.map(targetCode => {
          const targetConfig = allCodesToConfig[targetCode];
          return request(`group/${targetCode}`, {
            payload: {
              config: {
                activity_type: sourceConfig.activity_type,
                classroom_id: targetConfig?.classroom_id,
                standard_prefix: sourceConfig.standard_prefix,
                session_length: sourceConfig.session_length,
              }
            }
          });
        })
      );

      logEducatorDashboard("Educator dashboard: Group settings copied", {
        event: "group_settings_copied",
        sourceGroupCode: sourceCode,
        targetGroupCodes: selectedCodes,
      });
      setIsLoading(false);
      onSuccess();
      onClose();
    } catch (e) {
      setIsLoading(false);
      const errorMessage = e?.detail || e?.message || "An unknown error occurred.";
      logEducatorDashboard("Educator dashboard: Failed to copy group settings", {
        level: LogLevel.ERROR,
        event: "copy_settings_error",
        sourceGroupCode: sourceCode,
        errorMessage,
      });
      setError(errorMessage);
      logError("Failed to copy group settings", e, {
        category: LogCategory.ERROR,
        sourceCode,
        targetCodes: selectedCodes,
      });
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setSelectedCodes([]);
      setError(null);
      onClose();
    }
  };

  const sourceStandardInfo = getStandardInfo(sourceConfig);
  const otherCodes = Object.entries(allCodesToConfig || {})
    .filter(([code]) => code !== sourceCode)
    .sort(([codeA], [codeB]) => codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' }));

  return (
    <Modal show={isOpen} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton style={{ borderBottom: "none", paddingBottom: "8px" }}>
        <Modal.Title style={{ fontSize: "20px", fontWeight: "600", width: "100%", textAlign: "center" }}>
          Copy Group Settings
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "0 24px 24px 24px" }}>
        {/* Source Group Code */}
        <div style={{ marginBottom: "24px", paddingBottom: "24px", borderBottom: "1px solid #E5E7EB" }}>
          <div style={{ fontSize: "14px", fontWeight: "600", color: colors.nightlyWoods950, marginBottom: "12px" }}>
            Copying from:
          </div>
          <div style={{
            backgroundColor: "white",
            border: `3px solid ${colors.blue500}`,
            borderRadius: "12px",
            padding: "16px",
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            width: "100%"
          }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
              <div style={{
                alignSelf: "flex-end",
                backgroundColor: `${colors.blue100}`,
                borderRadius: "100px",
                color: `${colors.nightlyWoods500}`,
                fontSize: "14px",
                fontWeight: "600",
                lineHeight: "18px",
                padding: "2px 16px"
              }}>
                {sourceCode.toUpperCase()}
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                <img src={pie_chart_32} alt="Standard" style={{ width: "32px", height: "32px", flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{
                    color: `${colors.orange500}`,
                    fontSize: "10px",
                    fontWeight: "700",
                    letterSpacing: "0.5px",
                    marginBottom: "4px"
                  }}>
                    {sourceStandardInfo?.isCrazyCalculator
                      ? "ACTIVITY"
                      : sourceStandardInfo
                        ? `${sourceStandardInfo.type} - ${sourceStandardInfo.code}`
                        : "NO STANDARD SELECTED"}
                  </div>
                  <div style={{
                    color: colors.nightlyWoods950,
                    fontSize: "16px",
                    fontWeight: "600",
                    marginBottom: "8px"
                  }}>
                    {sourceStandardInfo?.isCrazyCalculator
                      ? sourceStandardInfo.name
                      : sourceStandardInfo
                        ? sourceStandardInfo.name
                        : "Select a standard to get started"}
                  </div>
                  <div style={{ fontSize: "12px", color: colors.nightlyWoods500 }}>
                    Session Length: <span style={{ fontWeight: "600" }}>{getSessionLengthLabel(sourceConfig.session_length)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Target Group Codes */}
        <div>
          <div style={{ fontSize: "14px", fontWeight: "600", color: colors.nightlyWoods950, marginBottom: "12px" }}>
            Select Group Codes to apply these settings:
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "12px",
            maxHeight: "400px",
            overflowY: "auto",
            padding: "4px"
          }}>
            {otherCodes.map(([code, config]) => {
              const standardInfo = getStandardInfo(config);
              const isSelected = selectedCodes.includes(code);

              return (
                <div
                  key={code}
                  onClick={() => toggleCodeSelection(code)}
                  style={{
                    backgroundColor: "white",
                    border: `3px solid ${isSelected ? `${colors.blue500}` : "transparent"}`,
                    borderRadius: "16px",
                    boxShadow: `0 1px 3px 0 rgba(14, 26, 49, 0.10), 0 1px 2px 0 rgba(14, 26, 49, 0.06)`,
                    padding: "12px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "8px",
                    width: "100%",
                    maxWidth: "360px"
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, maxWidth: "100%" }}>
                    <div style={{
                      alignSelf: "flex-end",
                      backgroundColor: `${colors.blue100}`,
                      borderRadius: "100px",
                      color: `${colors.nightlyWoods500}`,
                      fontSize: "14px",
                      fontWeight: "600",
                      lineHeight: "18px",
                      padding: "2px 16px"
                    }}>
                      {code.toUpperCase()}
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                      <img src={pie_chart_32} alt="Standard" style={{ width: "24px", height: "24px", flexShrink: 0, marginTop: "2px" }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          color: `${colors.orange500}`,
                          fontSize: "9px",
                          fontWeight: "700",
                          letterSpacing: "0.5px",
                          marginBottom: "2px"
                        }}>
                          {standardInfo?.isCrazyCalculator
                            ? "ACTIVITY"
                            : standardInfo
                              ? `${standardInfo.type} - ${standardInfo.code}`
                              : "NO STANDARD SELECTED"}
                        </div>
                        <div style={{
                          color: colors.nightlyWoods950,
                          fontSize: "14px",
                          fontWeight: "600",
                          marginBottom: "4px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}>
                          {standardInfo?.isCrazyCalculator
                            ? standardInfo.name
                            : standardInfo
                              ? standardInfo.name
                              : "Select a standard to get started"}
                        </div>
                        <div style={{ fontSize: "10px", color: colors.nightlyWoods500 }}>
                          Session Length: <span style={{ fontWeight: "600" }}>{getSessionLengthLabel(config.session_length)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Warning */}
        <div style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "8px",
          marginTop: "16px",
          padding: "12px",
          backgroundColor: "#FEF3C7",
          borderRadius: "8px"
        }}>
          <span style={{ alignSelf: "center", fontSize: "12px", color: colors.nightlyWoods500 }}>
            <spaan style={{ fontWeight: "bold" }}>⚠️ Warning:</spaan> This will overwrite the settings on selected groups (codes will remain unchanged)
          </span>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} style={{ marginTop: "16px" }}>
            {error}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer style={{ borderTop: "none", padding: "0 24px 24px 24px", gap: "12px" }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          style={{
            flex: 1,
            backgroundColor: "white",
            border: `1px solid ${colors.blue500}`,
            color: colors.nightlyWoods950,
            fontSize: "16px",
            fontWeight: "600",
            height: "40px",
            borderRadius: "8px"
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          disabled={isLoading || selectedCodes.length === 0}
          style={{
            flex: 1,
            backgroundColor: `${colors.blue500}`,
            border: "none",
            color: "white",
            fontSize: "16px",
            fontWeight: "600",
            height: "40px",
            borderRadius: "8px",
            opacity: isLoading || selectedCodes.length === 0 ? 0.5 : 1,
            cursor: isLoading || selectedCodes.length === 0 ? "not-allowed" : "pointer"
          }}
        >
          {isLoading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: "8px" }} />
              Applying...
            </>
          ) : (
            `Apply to ${selectedCodes.length} Group${selectedCodes.length !== 1 ? 's' : ''}`
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

